import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import UserGroupRow from '../rowComponents/UserGroupRow';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TableSortLabel, Paper, Skeleton, Box } from '@mui/material';

const initialFilters = {
    name: '',
    description: '',
    folder: '',
    lastUpdated: null
}

function UserGroupTable({userGroups = [], isLoading, onAction, onMembersClickAction, userReducer}){
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [filters, setFilters] = useState(initialFilters);
    const [searchText, setSearchText] = useState('');

    const isAdmin = userReducer?.userType?.toLowerCase() === 'admin';

    useEffect(() => {

        const totalRecords = userGroups.length;
        const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [userGroups, pageSize, currentPage]);

    const goToPage = (page) => {
        const totalRecords = userGroups.length;
        const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
        const safePage = Math.min(Math.max(page, 1), totalPages);
        if (safePage !== currentPage) {
            setCurrentPage(safePage);
        }
    }

    const goToNextPage = () => {
        goToPage(currentPage + 1);
    };

    const goToPreviousPage = () => {
        goToPage(currentPage - 1);
    };

    const handlePageSizeChange = (e) => {
        const value = parseInt(e.target.value, 10) || 10;
        setPageSize(value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    const clearAllFilters = () => {
        setFilters(initialFilters);
        setSearchText('');
        setCurrentPage(1);
    };

    const hasFilterValues = Object.values(filters).some(
        (val => (val || '').trim() !== '')
    );

    const hasSearchText = !!(searchText && searchText.trim());
    const anythingFiltered = hasFilterValues || hasSearchText;

    // --- Sorting ---
    const requestSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1);
    };

    const getSortableValue = (userGroup, key) => {
        switch (key) {
            case "name":
                return (userGroup.name || "").toLowerCase();
            case "description":
                return (userGroup.description || "").toLowerCase();
            case "folders":
                return (userGroup.x3Folders || []).join(",").toLowerCase();
            case "status":
                return userGroup.isActive ? 1 : 0;
            case "members":
                return userGroup.userCount || 0;
            case "lastUpdated":
                return userGroup.audit?.modifiedOn ? new Date(userGroup.audit.modifiedOn).getTime() : 0;
            default:
                return "";
        }
    };

    const SortHeader = ({ label, sortKey, style, align = 'left' }) => {
        const active = sortConfig.key === sortKey;
        const direction = active ? sortConfig.direction : 'asc';

        return (
            <TableCell style={style} align={align}>
                <TableSortLabel
                    active={active}
                    direction={direction}
                    onClick={() => requestSort(sortKey)}
                >
                    {label}
                </TableSortLabel>
            </TableCell>
        );
    };

    const filteredUserGroups = useMemo(() => {
        return userGroups.filter((userGroup) => {
            if (!searchText || !searchText.trim()) {
                return true;
            }
            const term = searchText.toLowerCase();

            return (
                (userGroup.name || '').toLowerCase().includes(term)
                || (userGroup.description || '').toLowerCase().includes(term)
                || (userGroup.x3Folders || []).some(folder => folder.toLowerCase().includes(term))
                || (userGroup.isActive ? 'true' : 'false' || '').toLowerCase().includes(term)
                || (userGroup.lastUpdated || '').toLowerCase().includes(term)
                || (userGroup.members || '').toLowerCase().includes(term)
            )
        });
    }, [userGroups, searchText]);

    // --- Sort (needs to be before pagination) ---
    const sortedUserGroups = useMemo(() => {
        const arr = [...filteredUserGroups];
        if (!sortConfig.key) return arr;

        arr.sort((a, b) => {
            const av = getSortableValue(a, sortConfig.key);
            const bv = getSortableValue(b, sortConfig.key);

            if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
            if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });

        return arr;
    }, [filteredUserGroups, sortConfig]);

    const totalRecords = sortedUserGroups.length;
    const totalPages = totalRecords === 0 ? 1 : Math.ceil(totalRecords / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalRecords);
    const pageUserGroups = sortedUserGroups.slice(startIndex, endIndex);

    return (
        <div className='s-table-center'>
            <div className="s-table-toolbar">
                <div className="s-table-toolbar-right">
                    <span className="s-table-record-count">
                        {isLoading
                        ? 'Loading user groups...'
                        : totalRecords === 0
                        ? 'No user groups'
                        : `Showing ${
                            totalRecords === 0 ? 0 : startIndex + 1
                            }–${endIndex} of ${totalRecords} user groups`}
                    </span>
                    <div className='s-table-page-size'>
                        <span>Rows per page:</span>
                        <select value={pageSize} onChange={handlePageSizeChange}>
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={150}>150</option>
                            <option value={500}>500</option>
                        </select>
                    </div>
                    <div className="s-table-pagination">
                        <button
                        type="button"
                        className='s-table-page-btn'
                        onClick={goToPreviousPage}
                        disabled={isLoading || totalRecords === 0 || currentPage === 1 }
                    >
                        ‹ Prev
                    </button>
                    <span className="s-table-page-info">
                        Page {totalRecords === 0 ? 0 : currentPage} of{' '}
                        {totalRecords === 0 ? 0 : totalPages}
                    </span>
                    <button
                        type="button"
                        className='s-table-page-btn'
                        onClick={goToNextPage}
                        disabled={
                            isLoading ||
                            totalRecords === 0 ||
                            currentPage >= totalPages
                        }
                    >
                        Next ›
                    </button>
                    </div>
                </div>
                <div className="s-table-toolbar-left">
                    {anythingFiltered && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={clearAllFilters}
                            title="Clear all filter fields"
                            style={{ marginLeft: '10px' }}
                        >
                            <i
                                className="fa fa-times"
                                aria-hidden="true"
                                style={{ marginRight: '5px' }}
                            >
                            </i>
                            Clear Filters
                        </button>
                    )}
                    <input
                        type="text"
                        className="s-table-search"
                        placeholder="Search user groups..."
                        value={searchText || ''}
                        onChange={handleSearchChange}
                        style={{ marginLeft: '10px' }}
                    />
                </div>
            </div>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee' }}>
                <Table className="s-table s-table-user-groups">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell align="center" sx={{ width: 50 }}></TableCell>
                            <TableCell align="center" sx={{ width: 50 }}>Edit</TableCell>
                            <TableCell align="center" sx={{ width: 50 }}>Delete</TableCell>

                            <SortHeader label="Name" sortKey="name" />
                            <SortHeader label="Description" sortKey="description" />
                            <SortHeader label="Folders" sortKey="folders" />
                            <SortHeader label="Status" sortKey="status" style={{ maxWidth: 120 }} />
                            <SortHeader label="Members" sortKey="members" style={{ maxWidth: 150 }} />
                            <SortHeader label="Last updated" sortKey="lastUpdated" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            // Skeleton loading rows
                            [...Array(pageSize)].map((_, index) => (
                                <TableRow key={`skeleton-${index}`}>
                                    <TableCell colSpan={9}>
                                        <Skeleton variant="rectangular" height={40} />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : pageUserGroups.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <Box sx={{ color: 'text.secondary' }}>
                                        No user groups found.
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageUserGroups.map((ug) => {
                                const id = ug.id || ug._id;
                                return (
                                    <UserGroupRow
                                        key={id}
                                        userGroup={ug}
                                        onAction={onAction}
                                        onMembersClickAction={onMembersClickAction}
                                    />
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )
}
export default connect((state) => state)(UserGroupTable);